<template>
  <div class="timer" :class="{ running: isRunning }">
    <span class="timer-display">{{ formatted }}</span>
    <button
      class="btn btn-sm btn-icon timer-btn"
      :title="isRunning ? t('timer.stop') : t('timer.start')"
      :disabled="loading"
      @click.stop="toggle"
    >
      <svg v-if="!isRunning" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
      <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
    </button>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'

export default {
  name: 'TaskTimer',
  props: {
    taskId:         { type: Number, required: true },
    timerSeconds:   { type: Number, default: 0 },
    timerStartedAt: { type: String, default: null },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const { t }      = useI18n()
    const loading    = ref(false)
    const localTotal = ref(0)
    let ticker = null

    const isRunning = computed(() => !!props.timerStartedAt)

    function syncLocal() {
      let t = props.timerSeconds || 0
      if (props.timerStartedAt) {
        const started = new Date(props.timerStartedAt).getTime()
        t += Math.max(0, Math.floor((Date.now() - started) / 1000))
      }
      localTotal.value = t
    }

    function startTicker() {
      stopTicker()
      ticker = setInterval(() => { localTotal.value++ }, 1000)
    }
    function stopTicker() {
      if (ticker) { clearInterval(ticker); ticker = null }
    }

    watch(() => [props.timerSeconds, props.timerStartedAt], () => {
      syncLocal()
      if (isRunning.value) startTicker()
      else stopTicker()
    }, { immediate: true })

    onMounted(() => { if (isRunning.value) startTicker() })
    onUnmounted(() => stopTicker())

    const formatted = computed(() => {
      const s = localTotal.value
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = s % 60
      return [h, m, sec].map(n => String(n).padStart(2, '0')).join(':')
    })

    async function toggle() {
      if (loading.value) return
      loading.value = true
      try {
        const res = isRunning.value
          ? await api.timerStop(props.taskId)
          : await api.timerStart(props.taskId)
        emit('update', res.data)
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    return { isRunning, loading, formatted, toggle, t }
  },
}
</script>

<style scoped>
.timer {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.timer-display { color: var(--muted); }
.timer.running .timer-display { color: var(--success); }
.timer-btn { color: var(--muted); }
.timer.running .timer-btn { color: var(--success); }
.timer-btn:hover { color: var(--text); background: var(--hover); }
</style>
